import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Params, Router} from '@angular/router';
import {LoaderQueueItem} from '../../../../types/LoaderQueueItem';
import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {CareproviderUnitService} from '../../../../services/careprovider-unit.service';
import {CareproviderHelper} from '../../../../services/careprovider.helper';
import {LoaderService} from '../../../../services/loader.service';
import {SearchReloadService} from '../../../../services/search/search-reload.service';
import {RoutesAddonsEnums, RoutesEnums, RoutesQueryParamsEnum} from '../../../../enums/RoutesEnums';
import {ToolsService} from '../../../../services/tools.service';
import {CareService} from '../../../../types/CareService';
import {CareproviderUnitItem} from '../../../../types/CareproviderUnitItem';
import {Subscription} from 'rxjs';
import {MetaTypesEnum} from '../../../../enums/SeoEnums';
import {SeoMetaService} from '../../../../services/seo-meta.service';
import {RedirectService} from '../../../../services/redirect.service';
import {GtmService} from '../../../../services/gtm.service';
import {Breadcrumb} from '../../../../types/Breadcrumb';
import {UrlBuilderService} from '../../../../services/url-builder.service';
import {BookingConfigurationData} from '../../../../types/BookingConfigurationData';
import {BookingTypeEnum, OpenHourFieldNameTypesEnum} from '../../../../enums/BookingEnums';
import {CustomerFeatureTypeEnum, IsCustomerDfpEnum} from '../../../../enums/AdEnums';
import {DLConversionTrackingEnum, DLPageTypeEnum, DLSegmentEnum} from '../../../../enums/DataLayerEnums';
import {DataLayerParams} from '../../../../types/DataLayerParams';
import {DataLayerParamsPageViewGA4} from '../../../../types/DataLayerParamsPageViewGA4';
import {PageViewsGa4ResolverService} from '../../../../services/page-views-ga4-resolver.service';
import {BookingHelperService} from '../../../../services/booking-helper.service';
import {DfpStateData} from '../../../../types/DfpStateData';
import {AdsStateService} from '../../../../services/ads-state.service';
import {CareServiceUrlResolverService} from '../../services/care-service-url-resolver.service';
import {ElementPositionEnum} from '../../../../enums/CssClassEnums';
import {AppConfigService} from '../../../../services/app-config.service';
import {WindowSizeService} from '../../../../services/window-size.service';
import {LayoutService} from '../../../layout/layout.service';
import {CareServiceSchemaService} from '../../services/care-service-schema.service';
import {CpruTabsService} from '../../../../services/cpru-tabs.service';
import {CpruTab} from '../../../../types/CpruTab';
import {EventActionEnum, EventCategoryEnum} from '../../../../enums/GaTrackingEnums';

@Component({
  selector: 'app-care-service',
  templateUrl: './care-service.component.html',
  styleUrls: ['./care-service.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CareServiceComponent implements OnInit, OnDestroy {
  public cpruTabsService = inject(CpruTabsService);
  public tabs = this.cpruTabsService.tabs;
  public highlightedSection = this.cpruTabsService.highlightedSection;
  protected careServiceId = signal<number>(null);
  protected isPreview = signal<boolean>(false);
  protected selectedCareService = signal<CareService>(null);
  protected careproviderUnit = signal<CareproviderUnitItem>(null);
  protected breadcrumbs = signal<Breadcrumb[]>([]);
  protected bookingConfigurationData = signal<BookingConfigurationData[]>([]);
  protected pageTitle = signal<string>(null);
  protected cpruTypes = signal<number[]>([]);
  protected topSectionCssClass = signal<string>(ElementPositionEnum.NORMAL);
  protected bottomSectionCssClass = signal<string>(ElementPositionEnum.NORMAL);
  protected logoUrl = computed<string>(() => {
    if (!this.careproviderUnit().caregiverUnitImages || !this.careproviderUnit().caregiverUnitImages.logoUrl) {
      return `${this.appConfigService.appConfig.amazonS3Url}assets/varden-logo.svg`;
    }
    return this.tools.getImageUrl(this.careproviderUnit().caregiverUnitImages.logoUrl);
  });
  protected mainImage = computed<string>(() => {
    if (!this.careproviderUnit().caregiverUnitImages || !this.careproviderUnit().caregiverUnitImages.thumbnailUrl) {
      return null;
    }
    return this.tools.getImageUrl(this.careproviderUnit().caregiverUnitImages.thumbnailUrl);
  });
  protected isInternal = computed<boolean>(() =>
    this.careproviderUnitService.isInternalCareService(
      this.selectedCareService(),
      this.careproviderUnit()?.bookingSettings,
    ),
  );
  protected displayBookingBtn = computed(
    () =>
      this.careproviderUnit() &&
      this.careproviderUnit().isCustomer &&
      !this.tools.isNil(this.bookingConfigurationData()) &&
      this.bookingConfigurationData().length > 0,
  );
  protected hasStaff = computed<boolean>(
    () => this.careproviderUnit()?.staffMembers && this.careproviderUnit().staffMembers.length > 0,
  );
  protected hasContact = computed<boolean>(
    () =>
      (this.careproviderUnit()?.caregiverContactInfo?.phoneNumbers &&
        this.careproviderUnit()?.caregiverContactInfo?.phoneNumbers.length > 0) ||
      (this.careproviderUnit()?.caregiverContactInfo?.websiteUrl !== null &&
        this.careproviderUnit()?.caregiverContactInfo?.websiteUrl !== ''),
  );
  protected hasOpenHours = computed<boolean>(
    () =>
      !this.tools.isNil(this.careproviderUnit()?.serviceHours) &&
      !this.tools.isNil(this.careproviderUnit()?.serviceHours[OpenHourFieldNameTypesEnum.SURGERY]) &&
      !this.tools.isNil(
        this.careproviderUnit()?.serviceHours[OpenHourFieldNameTypesEnum.SURGERY].filter(
          (hour) => hour.fromDay > 0 && hour.toDay > 0,
        ),
      ),
  );
  protected readonly dLPageTypeEnum = DLPageTypeEnum;
  protected readonly routesAddonsEnums = RoutesAddonsEnums;
  protected placeholderStyle = signal<{[klass: string]: any} | null | undefined>({display: 'none'});
  protected readonly eventCategoryEnum = EventCategoryEnum;
  protected readonly eventActionEnum = EventActionEnum;
  private urlBuilder = inject(UrlBuilderService);
  protected careProviderUnitUrl = computed<string[]>(() =>
    this.urlBuilder.CreateCareproviderUnitRouterLink(this.careproviderUnit()?.id, this.careproviderUnit()?.urlPath),
  );
  protected queryParams = computed(() => this.urlBuilder.CreateCareProviderUnitQueryParams(this.isPreview()));
  private careproviderUnitId = signal<number>(null);
  private lastCalledCareproviderUnitId = signal<number>(null);
  private trackedCareproviderUnitId = signal<number>(null);
  private trackedServiceId = signal<number>(null);
  private windowSize = inject(WindowSizeService);
  private size = this.windowSize.windowSize;
  private readonly mobileTabsThreshold = 992;
  protected isMobile = computed<boolean>(() => this.size()[0] < this.mobileTabsThreshold);
  private readonly greyBgThreshold = 768;
  private subscriptions: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private careproviderUnitService: CareproviderUnitService,
    private careproviderHelper: CareproviderHelper,
    private loaderService: LoaderService,
    private reloadService: SearchReloadService,
    private tools: ToolsService,
    private metaService: SeoMetaService,
    private redirect: RedirectService,
    private gtmService: GtmService,
    private pageViewGa4Resolver: PageViewsGa4ResolverService,
    private bookingHelper: BookingHelperService,
    private adsStateService: AdsStateService,
    private urlResolver: CareServiceUrlResolverService,
    private appConfigService: AppConfigService,
    private layoutService: LayoutService,
    private careServiceSchemaService: CareServiceSchemaService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    effect(
      () => {
        this.layoutService.setShouldUseGreyBackground(this.size()[0] >= this.greyBgThreshold);
      },
      {allowSignalWrites: true},
    );

    effect(() => {
      if (this.careproviderUnit() && this.selectedCareService()) {
        this.careServiceSchemaService.removeSchema();
        this.careServiceSchemaService.addSchema(this.careproviderUnit(), this.selectedCareService());
        this.tools.scrollTo();
      }
    });
  }

  public ngOnInit() {
    this.urlResolver.cleanUpUrl(this.route.snapshot);
    this.setListeners();
    this.prepareQueryDataAndLoadData();
    this.updateTabs();
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.resetNofollow();
    this.careServiceSchemaService.removeSchema();
  }

  @HostListener('window:scroll', ['$event'])
  public onScroll() {
    if (this.tabs() && this.tabs().length > 0) {
      const verticalOffset =
        window.pageYOffset || this.document.documentElement.scrollTop || this.document.body.scrollTop || 0;

      const coerce = 200;
      let currentSection: CpruTab = null;

      for (let i = this.tabs().length - 1; i >= 0; i--) {
        const elem = this.document.getElementById(`section-${this.tabs()[i].name}`);
        if (elem && verticalOffset + coerce > elem.offsetTop) {
          currentSection = this.tabs()[i];
          break;
        }
      }
      if (currentSection && currentSection.name != this.highlightedSection()) {
        this.cpruTabsService.setHighlightedSection(currentSection.name);
      }
    }
  }

  public handleTabsInViewport({target, visible}: {target: Element; visible: boolean}): void {
    if (visible) {
      this.topSectionCssClass.set(ElementPositionEnum.NORMAL);
      this.bottomSectionCssClass.set('hidden lg:hidden');
      this.placeholderStyle.set({display: 'none'});
    } else {
      const componentElement: HTMLElement = this.document.getElementById('top-section-container') as HTMLElement;
      const height = componentElement.offsetHeight;
      this.topSectionCssClass.set(ElementPositionEnum.FIXED_WITHOUT_SEARCH);
      this.bottomSectionCssClass.set('fixed lg:hidden');
      this.placeholderStyle.set({display: 'block', height: `${height}px`});
    }
  }

  public handleMoreClick(event: boolean): void {
    this.cpruTabsService.handleTabsIndexChange(RoutesAddonsEnums.CAREPROVIDER_ABOUT);
  }

  private setListeners(): void {
    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.urlResolver.cleanUpUrl(this.route.snapshot);
          this.prepareQueryDataAndLoadData();
          this.tools.scrollTo();
        }
      }),
    );
  }

  private prepareQueryDataAndLoadData(): void {
    this.reloadService.updateParams(this.route.snapshot.params, this.route.snapshot.queryParams);
    this.careproviderUnitId.set(parseInt(this.route.snapshot.params.id, 10));
    this.careServiceId.set(parseInt(this.route.snapshot.params.careServiceId, 10) || null);
    this.updateIsPreview(this.route.snapshot.queryParams);
    if (this.lastCalledCareproviderUnitId() !== this.careproviderUnitId()) {
      this.lastCalledCareproviderUnitId.set(this.careproviderUnitId());
      this.loadCareproviderUnitData();
    } else {
      this.processCareproviderUnitResults(this.careproviderUnit());
    }
  }

  private loadCareproviderUnitData(): void {
    const queryId: number = this.loaderService.generateQueueId();
    const queryItem: LoaderQueueItem = {
      id: queryId,
      title: '',
      description: '',
    };
    this.loaderService.addToQueue(queryItem);
    this.subscriptions.push(
      this.careproviderUnitService.getCareproviderUnitById(this.careproviderUnitId(), this.isPreview()).subscribe(
        (result) => {
          if (result && result.reviews && result.reviews.length > 0) {
            result.reviews = this.careproviderUnitService.prepareReviewsObject(result.reviews);
          }
          this.careproviderUnit.set(result);
          this.processCareproviderUnitResults(this.careproviderUnit());
          if (isPlatformBrowser(this.platformId) && this.careproviderUnit().isCustomer === true) {
            this.subscriptions.push(
              this.careproviderUnitService.setupTimeslotsUpdate(this.careproviderUnit()).subscribe((result) => {
                this.careproviderUnit.set(result.careproviderUnit);
                this.processCareproviderUnitResults(this.careproviderUnit());
              }),
            );
          }
          this.loaderService.removeFromQueue(queryId);
        },
        (error) => {
          console.error(error);
          this.careproviderUnit.set(null);
          this.loaderService.removeFromQueue(queryId);
        },
      ),
    );
  }

  private updateIsPreview(params: Params): void {
    this.isPreview.set(
      !this.tools.isNil(params) &&
        !this.tools.isNil(params[RoutesQueryParamsEnum.PREVIEW]) &&
        params[RoutesQueryParamsEnum.PREVIEW] === '1',
    );
  }

  private resetNofollow() {
    this.metaService.updateMetaTags([
      {
        value: 'index',
        type: MetaTypesEnum.NAME,
        field: 'robots',
      },
    ]);
  }

  private processCareproviderUnitResults(careproviderUnitData: CareproviderUnitItem): void {
    if (careproviderUnitData === null) {
      this.redirect.redirect(['/', RoutesEnums.NOTFOUND]);
      return;
    }
    this.gtmService.pushTag({ecommerce: null}, true);
    this.updateBookingConfigurationData();
    this.updateBookingConfigurationDataForCareServices();
    this.updateSelectedCareService();
    this.buildBreadcrumbs();
    this.redirectNotActive();
    this.redirectEmptyService();
    this.conditionalRedirectToNewUrl();
    this.pageTitle.set(this.careproviderHelper.updateMetaTags(this.selectedCareService(), this.careproviderUnit(), ''));
    this.trackPageView();
    this.updateAdsState();
  }

  private updateSelectedCareService(): void {
    if (
      !this.tools.isNil(this.careproviderUnit()) &&
      !this.tools.isNil(this.careproviderUnit().careServices) &&
      this.careproviderUnit() &&
      this.careproviderUnit().isCustomer === true
    ) {
      this.selectedCareService.set(
        this.careproviderUnit().careServices.find((item) => item.id === this.careServiceId()),
      );
    }
  }

  private buildBreadcrumbs() {
    this.breadcrumbs.set(
      this.careproviderUnit().breadcrumbs.map((breadcrumb) => ({
        anchor: breadcrumb.name,
        routeParams: [breadcrumb.url],
        routeQueryParams: null,
        nameSuffix: breadcrumb.nameSuffix,
      })),
    );

    if (!this.tools.isNil(this.selectedCareService())) {
      this.breadcrumbs.update((prev) => [
        ...prev,
        {
          anchor: this.selectedCareService().name,
          routeParams: null,
          routeQueryParams: null,
        },
      ]);
    }
  }

  private redirectNotActive(): void {
    if (this.isPreview()) {
      return;
    }

    if (this.careproviderUnit().isActive === false) {
      const breadcrumbItem: Breadcrumb = this.breadcrumbs()[this.breadcrumbs().length - 1];
      this.redirectToHigherBreadcrumbLevelOr404(breadcrumbItem);
    }
  }

  private redirectToHigherBreadcrumbLevelOr404(breadcrumbItem: Breadcrumb) {
    if (!this.tools.isNil(breadcrumbItem)) {
      this.redirect.redirect(breadcrumbItem.routeParams, breadcrumbItem.routeQueryParams);
    } else {
      this.redirect.redirect(['/', RoutesEnums.NOTFOUND]);
    }
  }

  private redirectEmptyService(): void {
    if (this.careServiceId() > 0 && this.tools.isNil(this.selectedCareService())) {
      this.redirect.redirect(
        this.urlBuilder.CreateCareproviderUnitRouterLink(this.careproviderUnit().id, this.careproviderUnit().urlPath),
      );
    }
  }

  private conditionalRedirectToNewUrl(): void {
    const urlPath: string = this.route.snapshot.params.urlPath;
    const serviceUrlPath: string = this.tools.isNil(this.selectedCareService())
      ? null
      : this.route.snapshot.params.careServiceUrlPath;
    const validServiceUrlPath: string = this.tools.isNil(this.selectedCareService())
      ? null
      : this.selectedCareService().urlPath;

    if (!this.areUrlPathsUpToDate(urlPath, this.careproviderUnit().urlPath, serviceUrlPath, validServiceUrlPath)) {
      let validUrl: string[] = this.urlBuilder.CreateCareproviderUnitRouterLink(
        this.careproviderUnit().id,
        this.careproviderUnit().urlPath,
      );

      if (!this.tools.isNil(this.selectedCareService())) {
        validUrl = this.urlBuilder.CreateCareServiceRouterLink(
          this.careproviderUnit().id,
          this.careproviderUnit().urlPath,
          this.selectedCareService().id,
          validServiceUrlPath,
        );
      }
      this.redirect.redirect(validUrl, this.queryParams(), false);
    }
  }

  private areUrlPathsUpToDate(
    urlPath: string,
    validUrlPath: string,
    serviceUrlPath?: string,
    validServiceUrlPath?: string,
  ): boolean {
    const basicValidation: boolean = this.tools.areStringsEqualIgnoringEncoding(urlPath, validUrlPath);
    const serviceValidation: boolean = this.tools.isNil(this.selectedCareService())
      ? true
      : this.tools.areStringsEqualIgnoringEncoding(serviceUrlPath, validServiceUrlPath);
    return basicValidation && serviceValidation;
  }

  private updateBookingConfigurationData(): void {
    this.bookingConfigurationData.set([]);
    this.careproviderUnit().bookingSettings.forEach((bookingSetting) => {
      const newBookingConfigurationData: BookingConfigurationData = {
        id: this.careproviderUnit().id,
        name: this.careproviderUnit().name,
        placeId: this.careproviderUnit().placeId,
        mainCaregiverUnitTypeId: this.careproviderUnit().mainCaregiverUnitType?.id,
        areaId: this.careproviderUnit().mainCaregiverUnitType?.area?.id,
        telephoneHours:
          this.careproviderUnit().serviceHours != null
            ? this.careproviderUnit().serviceHours[OpenHourFieldNameTypesEnum.TELEPHONE]
            : null,
        bookingSettings: bookingSetting,
        customerFeature: this.careproviderUnit().customerFeatures.find(
          (item) => item.type === CustomerFeatureTypeEnum.MODAL_BOOKING,
        ),
        allCustomerFeatures: this.careproviderUnit().customerFeatures,
        hsaName:
          this.careproviderUnit().hsaObjects && this.careproviderUnit().hsaObjects.length > 0
            ? this.careproviderUnit().hsaObjects[0].publicName
            : this.careproviderUnit().name,
        customListingName: this.careproviderUnit().listingName,
      };
      this.bookingConfigurationData.update((prev) => [...prev, newBookingConfigurationData]);
    });
  }

  private updateBookingConfigurationDataForCareServices(): void {
    const careServices: CareService[] = this.tools.clone(this.careproviderUnit().careServices);
    const newCareServices: CareService[] = [];
    careServices.forEach((careService) => {
      const bookingConfigurationData = [];
      bookingConfigurationData.push({
        id: this.careproviderUnit().id,
        careserviceId: careService.id,
        careserviceName: careService.name,
        careserviceTypeId: careService.careServiceTypeId,
        availableTimeSlots: careService.timeSlots,
        mainCaregiverUnitTypeId: this.careproviderUnit().mainCaregiverUnitType?.id,
        areaId: this.careproviderUnit().mainCaregiverUnitType?.area?.id,
        name: this.careproviderUnit().name,
        placeId: this.careproviderUnit().placeId,
        telephoneHours:
          this.careproviderUnit().serviceHours != null
            ? this.careproviderUnit().serviceHours[OpenHourFieldNameTypesEnum.TELEPHONE]
            : null,
        bookingSettings: careService.careServiceBookingSettings
          ? careService.careServiceBookingSettings
          : this.careproviderUnit() && this.careproviderUnit().bookingSettings[0]
            ? this.careproviderUnit().bookingSettings[0]
            : null,
        customerFeature: this.careproviderUnit().customerFeatures.find(
          (item) => item.type === CustomerFeatureTypeEnum.MODAL_BOOKING,
        ),
        allCustomerFeatures: this.careproviderUnit().customerFeatures,
        hsaName:
          this.careproviderUnit().hsaObjects && this.careproviderUnit().hsaObjects.length > 0
            ? this.careproviderUnit().hsaObjects[0].publicName
            : this.careproviderUnit().name,
      });

      newCareServices.push({...careService, bookingConfigurationData});
    });
    this.careproviderUnit.update((prev) => {
      return {...prev, careServices: newCareServices};
    });
  }

  private trackPageView(): void {
    if (this.careproviderUnit().timeslotsUpdated === true || this.careproviderUnit().isCustomer === false) {
      let shouldTrack: boolean = this.trackedCareproviderUnitId() !== this.careproviderUnit().id;
      const pageType =
        this.careproviderUnit() && this.careproviderUnit().isCustomer
          ? DLPageTypeEnum.CARE_PROVIDER_PAGE
          : DLPageTypeEnum.HSA_CARE_PROVIDER_PAGE;

      let customParams: DataLayerParams = {
        PageType: pageType,
        IsCustomer: this.careproviderUnit() && this.careproviderUnit().isCustomer,
        Segment: DLSegmentEnum.NOT_SET,
        CareProviderCategory: !this.tools.isNil(this.careproviderUnit().mainCaregiverUnitType)
          ? this.careproviderUnit().mainCaregiverUnitType.name
          : '',
      };
      customParams = this.updateDataLayerConversionOptions(customParams);
      customParams = this.updateDataLayerConversionTracking(customParams);

      const customGa4Params: DataLayerParamsPageViewGA4 = {
        pageType: pageType,
        pageTitle: this.pageTitle(),
        inferredCareProviderTypeAreaId: customParams.InferredCareproviderTypesArea,
        inferredCategoryTypeId: customParams.InferredCareproviderMainTypeID,
        mainCareProviderTypeId: customParams.InferredCareproviderMainTypeID,
        cpruId: customParams.CPRUID,
      };
      if (!this.tools.isNil(this.selectedCareService())) {
        customGa4Params.careServiceId = this.selectedCareService().id;
        customGa4Params.careServiceTypeId = this.selectedCareService().careServiceTypeId;
        shouldTrack = this.trackedServiceId() !== this.selectedCareService().id;
      }
      let timeslots: number = 0;
      if (!this.tools.isNil(this.careproviderUnit().careServices)) {
        this.careproviderUnit().careServices.forEach((item) => {
          timeslots += item.timeSlots == null ? 0 : item.timeSlots.length;
        });
      }
      customGa4Params.timeSlots = timeslots;
      if (shouldTrack) {
        this.trackedCareproviderUnitId.set(this.careproviderUnit().id);
        this.trackedServiceId.set(this.selectedCareService() ? this.selectedCareService().id : null);
        this.pageViewGa4Resolver.trackPageView(customGa4Params);
      }
    }
  }

  private updateDataLayerConversionOptions(customParams: DataLayerParams): DataLayerParams {
    customParams.ConversionOptionsBookingEmail = false;
    customParams.ConversionOptionsBookingLinkUntracked = false;
    customParams.ConversionOptionsBookingPhone = false;
    customParams.ConversionOptionsBookingIntegrated = false;
    customParams.ConversionOptionsBookingLinkTracked = false;
    customParams.ConversionOptionsExternalLink = false;
    customParams.ConversionOptionsListing = false;

    if (this.careproviderUnit().bookingSettings && this.careproviderUnit().bookingSettings.length > 0) {
      if (this.careproviderUnit().bookingSettings[0].bookingType.name === BookingTypeEnum.VARDEN) {
        customParams.ConversionOptionsBookingIntegrated = true;
      }

      if (this.careproviderUnit().bookingSettings[0].bookingType.name === BookingTypeEnum.EMAIL) {
        customParams.ConversionOptionsBookingEmail = true;
      }

      if (this.careproviderUnit().bookingSettings[0].bookingType.name === BookingTypeEnum.TEL) {
        customParams.ConversionOptionsBookingPhone = true;
      }

      customParams.ConversionOptionsBookingLinkTracked = this.bookingHelper.isBookingUrlTracked(
        this.careproviderUnit().bookingSettings[0],
      );
      customParams.ConversionOptionsBookingLinkUntracked = !this.bookingHelper.isBookingUrlTracked(
        this.careproviderUnit().bookingSettings[0],
      );
    }

    if (
      this.careproviderUnit().customerFeatures &&
      !this.tools.isNil(
        this.careproviderUnit().customerFeatures.find(
          (feature) => feature.type === CustomerFeatureTypeEnum.LISTA_DIG_DIRECT,
        ),
      )
    ) {
      customParams.ConversionOptionsListing = true;
    }

    if (
      this.careproviderUnit().caregiverContactInfo &&
      this.careproviderUnit().caregiverContactInfo.websiteUrl &&
      this.careproviderUnit().caregiverContactInfo.websiteUrl !== ''
    ) {
      customParams.ConversionOptionsExternalLink = true;
    }

    if (this.careproviderUnit().mainCaregiverUnitType) {
      customParams.InferredCareproviderMainTypeID = this.careproviderUnit().mainCaregiverUnitType.id;

      if (!this.tools.isNil(this.careproviderUnit().mainCaregiverUnitType.area)) {
        customParams.InferredCareproviderTypesArea = this.careproviderUnit().mainCaregiverUnitType.area.id;
      }

      if (!this.tools.isNil(this.careproviderUnit().mainCaregiverUnitType.segment)) {
        customParams.InferredCareproviderTypesSegment = this.careproviderUnit().mainCaregiverUnitType.segment.id;
      }
    }
    customParams.CPRUID = this.careproviderUnit().id;
    return customParams;
  }

  private updateDataLayerConversionTracking(customParams: DataLayerParams): DataLayerParams {
    if (customParams.ConversionOptionsBookingIntegrated === true) {
      customParams.ConversionTracking = DLConversionTrackingEnum.INTERNAL;
    } else if (customParams.ConversionOptionsBookingLinkTracked === true) {
      customParams.ConversionTracking = DLConversionTrackingEnum.EXTERNAL_BOOKING_TRACKED;
    } else if (customParams.ConversionOptionsBookingLinkUntracked === true) {
      customParams.ConversionTracking = DLConversionTrackingEnum.EXTERNAL_BOOKING_UNTRACKED;
    } else {
      customParams.ConversionTracking = DLConversionTrackingEnum.CLICKS_TRACKED;
    }
    return customParams;
  }

  private updateAdsState(): void {
    const dfpState: DfpStateData = this.adsStateService.getDefaultDfpState();
    dfpState.isCustomer =
      this.careproviderUnit() && this.careproviderUnit().isCustomer ? IsCustomerDfpEnum.TRUE : IsCustomerDfpEnum.FALSE;
    if (!this.tools.isNil(this.careproviderUnit().mainCaregiverUnitType)) {
      dfpState.careproviderUnitTypeId = this.careproviderUnit().mainCaregiverUnitType.id;
    }
    if (!this.tools.isNil(this.selectedCareService())) {
      dfpState.careServiceTypeId = this.selectedCareService().careServiceType.id;
      dfpState.pageModule = RoutesEnums.CARESERVICE;
    } else {
      dfpState.pageModule = RoutesEnums.CAREPROVIDER;
    }
    if (!this.tools.isNil(this.careproviderUnit().placeId)) {
      dfpState.placeId = this.careproviderUnit().placeId;
    }
    this.adsStateService.updateAdsState(dfpState);
  }

  private updateTabs(): void {
    const showImpressions = this.careproviderUnit() && this.careproviderUnit().externalReviewSummary !== null;

    const tabs: CpruTab[] = [];
    tabs.push({
      name: RoutesAddonsEnums.CARESERVICE_BOKA,
      label: 'Boka tid',
      mobileOnly: false,
      desktopOnly: false,
    });
    tabs.push({
      name: RoutesAddonsEnums.CARESERVICE_DETAILS,
      label: 'Detaljer',
      mobileOnly: false,
      desktopOnly: false,
    });
    tabs.push({
      name: RoutesAddonsEnums.CARESERVICE_ABOUT,
      label: 'Om mottagningen',
      mobileOnly: true,
      desktopOnly: false,
    });
    if (showImpressions) {
      tabs.push({
        name: RoutesAddonsEnums.CAREPROVIDER_IMPRESSION,
        label: 'Helhetsintryck',
        mobileOnly: true,
        desktopOnly: false,
      });
    }
    tabs.push({
      name: RoutesAddonsEnums.CARESERVICE_REVIEWS,
      label: 'Omdömen',
      mobileOnly: true,
      desktopOnly: false,
    });
    if (this.hasStaff()) {
      tabs.push({
        name: RoutesAddonsEnums.CARESERVICE_STAFF,
        label: 'Personal',
        mobileOnly: true,
        desktopOnly: false,
      });
    }
    if (this.hasContact()) {
      tabs.push({
        name: RoutesAddonsEnums.CARESERVICE_CONTACT,
        label: 'Kontakt & öppettider',
        mobileOnly: true,
        desktopOnly: false,
      });
    }

    tabs.push({
      name: RoutesAddonsEnums.CARESERVICE_MORE,
      label: 'Fler tjänster',
      mobileOnly: false,
      desktopOnly: false,
    });

    this.cpruTabsService.updateTabs(tabs);
  }
}
